import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Table, TableBody, TablePagination, TableProvider } from '@percent/lemonade'
import { usePointsRewardValue } from '@percent/partner-dashboard/common/hooks/usePointsRewardValue/usePointsRewardValue'
import { GivingAction } from '@percent/partner-dashboard/services/givingActions/givingActionsService.types'
import { GivingActionsTableRow } from './GivingActionsTableRow/GivingActionsTableRow'

export type GivingActionsTableProps = Readonly<{
  data: GivingAction[]
  isLoading: boolean
  canManage: boolean
  totalResults: number | null
  searchQuery: string
  nextPage: (() => void) | null
  previousPage: (() => void) | null
  onCreate: () => void
}>

export function GivingActionsTable({
  data,
  isLoading,
  canManage,
  totalResults,
  searchQuery,
  nextPage,
  previousPage,
  onCreate
}: GivingActionsTableProps) {
  const { t } = useTranslation()
  const { isLoading: isRewardLoading, isRewardConfigured, formatReward } = usePointsRewardValue()

  const columns = useMemo(
    () => [
      { id: 'name', content: t('table.header.name'), props: { width: '20%' } },
      { id: 'description', content: t('table.header.description'), props: { width: '25%' } },
      { id: 'frequency', content: t('table.header.frequency'), props: { width: '11%' } },
      {
        id: 'rewards',
        content: t(isRewardConfigured ? 'table.header.rewardValue' : 'table.header.rewards'),
        props: { width: '10%' }
      },
      { id: 'status', content: t('table.header.status'), props: { width: '11%' } },
      { id: 'createdAt', content: t('table.header.created'), props: { width: '13%' } },
      { id: 'actions', content: ' ', props: { width: '10%' } }
    ],
    [t, isRewardConfigured]
  )

  return (
    <TableProvider
      columns={columns}
      totalResults={totalResults}
      isLoading={isLoading}
      illustration={searchQuery ? 'no-results' : 'nothing-to-see'}
      emptyTableTitle={
        searchQuery
          ? t('typography.noSearchTableListTitle', { searchTerm: searchQuery })
          : t('typography.givingActions.emptyTitle')
      }
      emptyTableDescription={
        searchQuery ? t('typography.noSearchTableListDescription') : t('typography.givingActions.emptyDescription')
      }
      emptyTableButton={
        canManage && !searchQuery ? { label: t('button.createGivingAction'), onClick: onCreate } : undefined
      }
    >
      <Table>
        <TableBody>
          {data?.map(givingAction => (
            <GivingActionsTableRow
              key={givingAction.id}
              givingAction={givingAction}
              canManage={canManage}
              formatReward={formatReward}
              isRewardLoading={isRewardLoading}
            />
          ))}
        </TableBody>
      </Table>
      <TablePagination
        totalResultsText={t('table.results', { count: totalResults ?? 0 })}
        previousPage={previousPage}
        nextPage={nextPage}
      />
    </TableProvider>
  )
}
